import Dexie from 'dexie';
/**
 * Local-only cache of external calendar data.
 *
 * This is a SEPARATE IndexedDB database, and that is the whole point. It is
 * not part of StorageAdapter, so it cannot be reached by SyncEngine; it is not
 * in the multi-file cloud layout, so it is never uploaded; and it is not in
 * ExportData, so it never lands in a JSON export either. A connected work
 * calendar's meeting titles stay on the device that connected it.
 *
 * Being a separate database also means adding it costs the main TrackMindDB
 * no schema version bump and no migration — zero risk to existing user data.
 *
 * Everything here is disposable: if it is lost, the next refresh rebuilds it
 * from the provider. Accounts and sources are kept here too (rather than in
 * preferences) because a connection is inherently per-device: the OAuth token
 * lives in this browser's localStorage and cannot be shared.
 */
const CACHE_DB_NAME = 'TrackMindCalendarCache';
class CalendarDatabase extends Dexie {
    accounts;
    sources;
    events;
    constructor() {
        super(CACHE_DB_NAME);
        this.version(1).stores({
            accounts: 'id, email',
            sources: 'id, account_id, calendar_id',
            events: 'id, source_id, account_id, date, [source_id+date]',
        });
    }
}
export class CalendarCache {
    db;
    constructor() {
        this.db = new CalendarDatabase();
    }
    async initialize() {
        await this.db.open();
    }
    // ── Accounts ────────────────────────────────────────────────────────────
    async getAccounts() {
        return this.db.accounts.toArray();
    }
    async saveAccount(account) {
        await this.db.accounts.put(account);
    }
    /** Removing an account takes its calendars and their cached events with it. */
    async deleteAccount(accountId) {
        await this.db.transaction('rw', this.db.accounts, this.db.sources, this.db.events, async () => {
            await this.db.events.where('account_id').equals(accountId).delete();
            await this.db.sources.where('account_id').equals(accountId).delete();
            await this.db.accounts.delete(accountId);
        });
    }
    // ── Sources (individual calendars) ──────────────────────────────────────
    async getSources() {
        return this.db.sources.toArray();
    }
    async saveSource(source) {
        await this.db.sources.put(source);
    }
    async saveSources(sources) {
        await this.db.sources.bulkPut(sources);
    }
    async deleteSource(sourceId) {
        await this.db.transaction('rw', this.db.sources, this.db.events, async () => {
            await this.db.events.where('source_id').equals(sourceId).delete();
            await this.db.sources.delete(sourceId);
        });
    }
    // ── Events ──────────────────────────────────────────────────────────────
    async getEvents() {
        return this.db.events.toArray();
    }
    /** Events touching a single local day. */
    async getEventsForDate(date) {
        return this.db.events.where('date').equals(date).toArray();
    }
    /** Events touching any day in `[startDate, endDate]` inclusive. */
    async getEventsInRange(startDate, endDate) {
        return this.db.events.where('date').between(startDate, endDate, true, true).toArray();
    }
    async saveEvents(events) {
        if (events.length === 0)
            return;
        await this.db.events.bulkPut(events);
    }
    async deleteEvents(eventIds) {
        if (eventIds.length === 0)
            return;
        await this.db.events.bulkDelete(eventIds);
    }
    /**
     * Remove every row belonging to the given events, including the extra rows
     * a multi-day event occupies (`<base>::<date>`). Callers use this to clear
     * before re-inserting, so a meeting moved to another day leaves no ghost on
     * the day it came from.
     */
    async deleteEventsByBaseId(baseIds) {
        if (baseIds.length === 0)
            return;
        const keys = await this.db.events
            .filter((e) => baseIds.some((base) => e.id === base || e.id.startsWith(`${base}::`)))
            .primaryKeys();
        if (keys.length > 0)
            await this.db.events.bulkDelete(keys);
    }
    /** Drop a source's cached events — used before a full (non-incremental) refetch. */
    async clearEventsForSource(sourceId) {
        await this.db.events.where('source_id').equals(sourceId).delete();
    }
    /**
     * Forget events outside the window we keep. Without this the cache grows
     * forever as the rolling window advances.
     */
    async pruneOutside(startDate, endDate) {
        const stale = await this.db.events
            .filter((e) => e.date < startDate || e.date > endDate)
            .primaryKeys();
        if (stale.length > 0)
            await this.db.events.bulkDelete(stale);
        return stale.length;
    }
    /** Wipe everything — used when the user disconnects all calendars. */
    async clearAll() {
        await this.db.transaction('rw', this.db.accounts, this.db.sources, this.db.events, async () => {
            await this.db.events.clear();
            await this.db.sources.clear();
            await this.db.accounts.clear();
        });
    }
}
//# sourceMappingURL=calendar-cache.js.map